import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Popup from "reactjs-popup";

import BoxCard from "shared/components/box/BoxCard.js";
import ButtonMflClub from "shared/components/buttons/ButtonMflClub.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import MiscClubPerformance from "shared/components/misc/MiscClubPerformance";
import MiscCompetitionRanking from "shared/components/misc/MiscCompetitionRanking";
import MiscDepthField from "shared/components/misc/MiscDepthField";
import { useNavigate } from "react-router-dom";

import { getFullCompetitionsLimited, getMatches, getOverallVsGdRates } from "shared/services/api-assistant.js";

import MiscClubStats from "shared/components/misc/MiscClubStats.js";

/*interface PopupClubProps {
  className?: string;
  club: any;
  trigger?: React.ReactNode;
  open?: boolean;
  onClose?: () => void;
}*/

const PopupClub = ({ className = "", club, trigger, open, onClose }) => {
  const navigate = useNavigate();

  const [competitions, setCompetitions] = useState(null);

  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [matches, setMatches] = useState(null);
  const [types, setTypes] = useState(["LEAGUE", "CUP"]);
  const [overallVsGdRates, setOverallVsGdRates] = useState(null);
  const [view, setView] = useState("ranking");

  const fetchClubStandings = () => {
    getFullCompetitionsLimited({
      handleSuccess: (v) => setCompetitions(v.data.getCompetitions),
      handleError: (e) => console.log(`Error fetching standings for club ID ${club.id}:`, e),
      params: { participants: [club.id] },
    });
  };

  const fetchOverallVsGdRates = () => {
    getOverallVsGdRates({
      handleSuccess: (d) => setOverallVsGdRates(d.data.getOverallVsGdRates),
      handleError: console.log,
      params: { engine: "15" },
    });
  };

  const fetchMatches = () => {
    if (!club || !selectedCompetition) return;

    setMatches(null);

    getMatches({
      handleSuccess: (d) => setMatches(d.data.getMatches),
      handleError: console.log,
      params: {
        limit: 20,
        clubs: [club.id],
        statuses: ["LIVE", "ENDED"],
        types: types,
        minDate: selectedCompetition?.startingDate?.slice(0, 10),
        maxDate: endDate?.slice(0, 10),
      },
    });
  };

  useEffect(() => {
    if (open) {
      fetchClubStandings();
      fetchOverallVsGdRates();
    }
  }, [open]);

  useEffect(() => {
    if (open && selectedCompetition) {
      fetchMatches();
    }
  }, [open, selectedCompetition, types]);

  const getCompetitionBlocks = () => {
    if (!competitions || competitions.length === 0) return [];

    const maxSeason = Math.max(...competitions.map((c) => c.season));
    const filtered = competitions.filter((c) => c.season === maxSeason);

    const sorted = filtered.sort((a, b) => (a.type === "LEAGUE" ? -1 : b.type === "LEAGUE" ? 1 : 0));

    return sorted.map((competition) => (
      <MiscCompetitionRanking key={competition.id} competition={competition} clubToHighlight={[club.id]} />
    ));
  };

  return (
    <div className="PopupClub d-flex flex-fill">
      {club && (
        <Popup
          trigger={trigger}
          modal
          closeOnDocumentClick={false}
          className={`fade-in popup-xl ${className}`}
          onOpen={fetchClubStandings}
          {...(open !== undefined ? { open, onClose } : {})}
        >
          {(close) => (
            <div>
              <div className="d-flex flex-row mb-3">
                <div className="flex-grow-1">
                  <h2 className="text-white">
                    <i className="bi bi-buildings-fill me-1" />
                    {club.name}
                  </h2>
                </div>
                <div className="flex-grow-0">
                  <button className="btn" onClick={close}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>

              <div className="d-flex flex-column flex-sm-row m-1 mb-2">
                <div className="d-flex flex-column flex-basis-200">
                  <div className="d-flex justify-content-center mb-3">
                    <img
                      src={`https://d13e14gtps4iwl.cloudfront.net/u/clubs/${club.id}/logo.webp`}
                      alt={`${club.name} logo`}
                      style={{ width: "70%", maxWidth: "250px" }}
                    />
                  </div>

                  <div className="px-2 mb-3 d-flex flex-row justify-content-center align-items-center">
                    <div className="me-1">
                      <ButtonMflClub clubId={club.id} />
                    </div>
                    <button className="btn btn-sm btn-info text-white" onClick={() => navigate(`/club/${club.id}`)}>
                      Open <i className="bi bi-caret-right-fill"></i>
                    </button>
                  </div>
                </div>

                <div className="d-flex flex-column flex-fill">
                  <div className="d-flex flex-column-reverse flex-column flex-md-row align-items-end justify-content-end px-2">
                    <div className=" mb-1">
                      <ButtonSelect
                        selected={view}
                        onChange={(v) => setView(v)}
                        buttons={{
                          ranking: "Ranking and projection",
                          performance: "Performance",
                          squadDepth: "Squad depth",
                        }}
                      />
                    </div>
                  </div>

                  {view === "ranking" && <MiscClubStats club={club} />}

                  {view === "ranking" && (
                    <BoxCard
                      className="mb-2"
                      title={"Ranking and projection"}
                      content={
                        competitions ? (
                          <div className="d-flex flex-column flex-fill">{getCompetitionBlocks()}</div>
                        ) : (
                          <div style={{ height: 300 }} className="w-100">
                            <LoadingSquare height={300} />
                          </div>
                        )
                      }
                    />
                  )}

                  {view === "performance" && <MiscClubPerformance clubId={club.id} clubType={club.type} />}

                  {view === "squadDepth" && (
                    <BoxCard className="h-md-100 p-0" noPadding={true} content={<MiscDepthField clubId={club.id} />} />
                  )}
                </div>
              </div>
            </div>
          )}
        </Popup>
      )}
    </div>
  );
};

export default PopupClub;
